import React, { MouseEvent, ReactNode, useState } from 'react';
import styled from 'styled-components';
// --------------------------------------------------------------------------------
import { String, StringProps } from './String';
import { config, Icons } from '../index';
import { DropDown, DropDownProps } from './DropDown';

export interface ButtonProps {
  /* Button text */
  label?: StringProps;
  /* Node props to render as component */
  node?: ReactNode;
  /* Button shape type */
  shape?: 'rounded' | 'square' | 'circular';
  /* Button type */
  type?: 'primary' | 'outline' | 'subtle' | 'transparent';
  /* Button icon */
  icon?: string;
  /* Button fixed width */
  width?: string;
  /* Button min width */
  minWidth?: string;
  /* Button max width */
  maxWidth?: string;
  /* Button split mode */
  isSplitButton?: boolean;
  /* Button size */
  size?: 'small' | 'medium' | 'large';
  /* Button background color */
  backgroundColor?: string;
  /* Button margin */
  margin?: string;
  /* Button padding */
  padding?: string;
  /* Button text color */
  color?: string;
  /* Button display mode */
  display?: 'flex' | 'none';
  /* Disable button */
  disabled?: boolean;
  /* Button border */
  border?: string;
  /* Button shadow */
  boxShadow?: string;
  /* Button additional class props */
  className?: string;
  /* Disable | active condition */
  isActive?: boolean;
  /* Button default opacity */
  opacity?: number;
  // --------------------------------------------------------------------------------
  /* Button dropdown content */
  dropdownOptions?: DropDownLink[];
  /* Button dropdown content configuration */
  dropdownConfig?: DropDownProps;
  /* Icon additional configuration */
  iconConfig?: ButtonIconProps;
  // --------------------------------------------------------------------------------
  /* Button click event */
  onClick?: (event?: MouseEvent) => void;
  /* Button click event */
  onSecondaryClick?: (event?: MouseEvent) => void;
}

export interface BtnButtonIconProps extends ButtonPrivateProps {
  fontSize?: string;
  transform?: string;
}

export interface ButtonIconProps {
  icon?: string;
  fill?: string;
  size?: number;
  hoverFill?: string;
  hoverStroke?: string;
  transform?: string;
  onClick?: (event?: MouseEvent) => void;
}
export interface DropDownLink {
  title: StringProps;
  onClick: (event?: MouseEvent) => void;
}

interface ButtonPrivateProps extends ButtonProps {
  border?: string;
  borderRadius?: string;
}

const StyledButton = styled.div<ButtonPrivateProps>`
  display: flex;
  flex: 1;
  position: relative;
  align-items: center;
  justify-content: center;
  font-family: ${config.fontFamilies};
  display: inline-block;
  line-height: 1;
  transition: ${config.transitionMedium};
  user-select: none;
  vertical-align: bottom;
  background-position: center;

  background-color: ${(props) => props.backgroundColor};
  border: ${(props) => props.border};
  padding: ${(props) => props.padding};
  margin: ${(props) => props.margin};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  display: ${(props) => props.display};
  border-bottom-left-radius: ${(props) => props.borderRadius};
  border-top-left-radius: ${(props) => props.borderRadius};
  border-bottom-right-radius: ${(props) =>
    props.isSplitButton ? 'unset' : props.borderRadius};
  border-top-right-radius: ${(props) =>
    props.isSplitButton ? 'unset' : props.borderRadius};
  box-shadow: ${(props) => props.boxShadow};

  &:hover {
    opacity: ${(props) => (props.disabled ? 0.5 : 0.8)};
    background-color: ${(props) =>
      props.type === 'primary' || props.type === 'transparent'
        ? props.backgroundColor
        : config.muted};
  }

  &:active {
    opacity: ${(props) => (props.disabled ? 0.5 : 0.7)};
    background-size: 100%;
    transition: background 2s;

    &:after {
      opacity: 1;
    }
  }
`;

const ComponentWrapper = styled.div<BtnButtonIconProps>`
  display: flex;
`;

const ActionWrapper = styled.div<BtnButtonIconProps>`
  display: flex;
  align-items: center;
  padding: 0 ${config.spacingSmall} 0 ${config.spacingXXSmall};
  border-bottom-right-radius: ${(props) => props.borderRadius};
  border-top-right-radius: ${(props) => props.borderRadius};
  background-color: ${(props) => props.backgroundColor};
  border: ${(props) => (props.type === 'outline' ? props.border : 'none')};
  border-left: ${(props) =>
    props.type === 'transparent' || props.type === 'outline'
      ? `2px solid ${config.primary}`
      : `2px solid ${config.white}`};
  cursor: pointer;

  &:hover {
    opacity: ${(props) => (props.disabled ? 0.5 : 0.8)};
    background-color: ${(props) =>
      props.type === 'primary' || props.type === 'transparent'
        ? props.backgroundColor
        : config.muted};
  }
`;

const ActionIconWrapper = styled.div<BtnButtonIconProps>`
  transition: ${config.transitionShort};
  transform: ${(props) =>
    props.isActive ? `rotate(180deg)` : props.transform ?? `rotate(45deg)`};
  transform-origin: center;
`;

const StyledWrapper = styled.div<ButtonProps>`
  position: relative;
  width: ${(props) => props.width};
  min-width: ${(props) => props.minWidth};
  max-width: ${(props) => props.maxWidth};
  margin: ${(props) => props.margin};
`;

// Primary UI component for user interaction
export const Button: React.FC<ButtonProps> = ({
  label,
  node,
  shape = 'circular',
  type = 'primary',
  width = 'fit-content',
  minWidth,
  maxWidth,
  size = 'medium',
  icon,
  backgroundColor,
  margin,
  padding,
  disabled = false, // disabled state
  display = 'flex',
  boxShadow,
  dropdownOptions,
  dropdownConfig,
  iconConfig,
  opacity = 1,
  isSplitButton = false,
  border = `1px solid ${config.primary}`,
  color = `${config.darkMuted}`,
  // --------------------------------------------------------------------------------
  onClick,
  onSecondaryClick,
  // additional props
  ...props
}) => {
  // --------------------------------------------------------------------------------
  // 📌  Default configuration | small button
  // --------------------------------------------------------------------------------
  let fontSize = config.fontXXSmall;
  let borderRadius = `${config.spacingXXXXLarge}`;
  let btnPadding = `${config.fontSmall} ${config.fontMedium}`;
  let bgColor = `${config.primary}`;

  //  Overrides to defaults
  if (backgroundColor) bgColor = backgroundColor;

  if (type === 'outline' || type === 'subtle' || type === 'transparent') {
    color = `${config.primary}`;
    bgColor = `${config.transparent}`;
  }

  if (type === 'primary') {
    color = `${config.white}`;
  }

  if (type === 'subtle' || type === 'transparent') {
    border = 'none';
    bgColor = 'transparent';
  }

  if (shape === 'square') borderRadius = `${config.spacingSmall}`;
  if (shape === 'rounded') borderRadius = `${config.spacingXMedium}`;

  if (size === 'medium') {
    fontSize = config.spacingXMedium;
    btnPadding = `${config.spacingLarge} ${config.spacingXXXLarge}`;
  }
  if (size === 'large') {
    fontSize = config.spacingXLarge;
    btnPadding = `${config.spacingLarge} ${config.spacingXXXLarge}`;
  }

  //  Overrides to defaults
  if (padding) btnPadding = padding;

  // --------------------------------------------------------------------------------
  const [isActive, setActive] = useState(false);

  const handleDropdownAction = (event: MouseEvent | undefined) => {
    if (disabled) return;

    onSecondaryClick?.(event);
    setActive(!isActive);
  };

  const handleActions = (event: MouseEvent | undefined) => {
    if (disabled) return; // ⚠️ break if button is disabled
    if (dropdownOptions && !isSplitButton) {
      setActive(!isActive);
    }
    onClick?.(event);
  };

  return (
    <StyledWrapper
      width={width}
      minWidth={minWidth}
      maxWidth={maxWidth}
      margin={margin}
    >
      <ComponentWrapper>
        <StyledButton
          shape={shape}
          size={size}
          backgroundColor={bgColor}
          border={border}
          borderRadius={borderRadius}
          padding={btnPadding}
          disabled={disabled}
          display={display}
          opacity={opacity}
          dropdownOptions={dropdownOptions}
          type={type}
          isSplitButton={isSplitButton}
          // --------------------------------------------------------------------------------
          onClick={handleActions}
          // any additional props that override the defaults
          {...props}
        >
          {!node && (
            <String
              fontSize={fontSize}
              fontWeight="700"
              color={label?.color || color}
              limit={1}
              cursor={disabled ? 'default' : 'pointer'}
              width="100%"
              align="center"
              margin={
                isSplitButton || !dropdownOptions
                  ? '0'
                  : `0 ${config.spacingSmall} 0 0`
              }
              {...label}
            />
          )}
          {node && node}
          {icon && (
            <Icons
              icon={icon}
              onClick={disabled ? undefined : () => {}} // on hoover behavior
              fill={label?.color || color}
              size={32}
              {...iconConfig}
            />
          )}

          {dropdownOptions && !isSplitButton && (
            <ActionIconWrapper
              isActive={isActive}
              transform={iconConfig?.transform}
            >
              <Icons
                icon="close"
                fill={label?.color || color}
                size={16}
                onClick={() => {}} // default icon behavior for onClick
                {...iconConfig}
              />
            </ActionIconWrapper>
          )}
        </StyledButton>
        {dropdownOptions && isSplitButton && (
          <ActionWrapper
            isActive={isActive}
            backgroundColor={bgColor}
            borderRadius={borderRadius}
            type={type}
            border={border}
            padding={btnPadding}
            isSplitButton={isSplitButton}
            onClick={handleDropdownAction}
          >
            <ActionIconWrapper isActive={isActive}>
              <Icons
                icon="close"
                fill={label?.color || color}
                hoverFill={label?.color || color}
                size={16}
                onClick={() => {}} // default icon behavior for onClick
                {...iconConfig}
              />
            </ActionIconWrapper>
          </ActionWrapper>
        )}
      </ComponentWrapper>
      <DropDown
        dropDownProps={dropdownOptions}
        isActive={isActive}
        width="max-content"
        {...dropdownConfig}
        onClick={() => setActive(!isActive)}
      />
    </StyledWrapper>
  );
};
